import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import axios from 'axios';
import Button from '../../../component/button/button';
import CustomInput from '../../../component/CustomInput/CustomInput';
import {
  createHierarchyItem,
  updateHierarchyItem,
  listAllHierarchyItems,
} from '../../../api/subject';
import {
  successMessage,
  errorMessageShow,
  LoaderComponent,
} from '../../../utils/Helper';
import { HierarchyItem } from './hierarchyUtils';
import '../../Modal/Modal.scss';

interface Option {
  value: string;
  label: string;
}

interface NodeModalProps {
  subjectId: string;
  level: number;
  levelLabel: string;
  parentLabel?: string;
  mode: 'create' | 'edit';
  item?: HierarchyItem;
  onClose: () => void;
  onSaved: () => void;
}

const NodeModal: React.FC<NodeModalProps> = ({
  subjectId,
  level,
  levelLabel,
  parentLabel,
  mode,
  item,
  onClose,
  onSaved,
}) => {
  const needsParent = level > 1;

  const [name, setName] = useState(item?.name ?? '');
  const [parentId, setParentId] = useState<string | null>(
    item?.parent_id ?? null
  );
  const [parentOptions, setParentOptions] = useState<Option[]>([]);
  const [loadingParents, setLoadingParents] = useState(false);
  const [saving, setSaving] = useState(false);
  const [nameError, setNameError] = useState('');
  const [parentError, setParentError] = useState('');

  useEffect(() => {
    if (!needsParent) return;
    const loadParents = async () => {
      try {
        setLoadingParents(true);
        const items = (await listAllHierarchyItems(subjectId, {
          level: level - 1,
        })) as HierarchyItem[];
        setParentOptions(items.map(i => ({ value: i.id, label: i.name })));
      } catch (error) {
        console.error('Error loading parent items:', error);
      } finally {
        setLoadingParents(false);
      }
    };
    loadParents();
  }, [needsParent, subjectId, level]);

  const selectedParent = useMemo(
    () => parentOptions.find(o => o.value === parentId) ?? null,
    [parentOptions, parentId]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    let invalid = false;
    if (!name.trim()) {
      setNameError('Name is required.');
      invalid = true;
    }
    if (needsParent && !parentId) {
      setParentError(`Please choose a ${parentLabel || 'parent'}.`);
      invalid = true;
    }
    if (invalid) return;

    try {
      setSaving(true);
      if (mode === 'create') {
        await createHierarchyItem(subjectId, {
          level,
          name: name.trim(),
          parent_id: needsParent ? parentId : null,
        });
        successMessage(`${levelLabel} created.`);
      } else if (item) {
        await updateHierarchyItem(subjectId, item.id, {
          name: name.trim(),
          ...(needsParent ? { parent_id: parentId } : {}),
        });
        successMessage(`${levelLabel} updated.`);
      }
      onSaved();
      onClose();
    } catch (error) {
      if (axios.isAxiosError(error)) {
        errorMessageShow(
          error.response?.data?.message || 'An unexpected error occurred.'
        );
      } else {
        console.error(error);
      }
    } finally {
      setSaving(false);
    }
  };

  const noParents = needsParent && !loadingParents && parentOptions.length === 0;

  return (
    <div className="modal-backdrop">
      <div className="modal">
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <h3 className="text-lg font-semibold mb-4">
              {mode === 'create' ? `Add ${levelLabel}` : `Edit ${levelLabel}`}
            </h3>

            {needsParent && (
              <div className="text-left mb-3">
                <label>
                  {parentLabel} <span className="text-red-500">*</span>
                </label>
                {loadingParents ? (
                  <LoaderComponent size="sm" label="Loading…" />
                ) : noParents ? (
                  <p className="text-sm text-amber-600 mt-1">
                    No {parentLabel?.toLowerCase()} exists yet. Create one first.
                  </p>
                ) : (
                  <Select
                    options={parentOptions}
                    value={selectedParent}
                    onChange={selected => {
                      setParentId((selected as Option | null)?.value ?? null);
                      setParentError('');
                    }}
                    placeholder={`Select ${parentLabel?.toLowerCase()}`}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      menuPortal: base => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                )}
                {parentError && (
                  <p className="text-red-500 text-xs mt-1">{parentError}</p>
                )}
              </div>
            )}

            <div className="text-left mb-3">
              <label>
                {levelLabel} name <span className="text-red-500">*</span>
              </label>
              <CustomInput
                value={name}
                placeholder={`${levelLabel} name`}
                onChange={e => {
                  setName(e.target.value);
                  setNameError('');
                }}
                error={nameError}
                style={{ height: 42 }}
              />
            </div>
          </div>

          <div className="modal-footer">
            <div className="text-right flex justify-end gap-2">
              <Button
                text={saving ? 'Saving…' : mode === 'create' ? 'Create' : 'Update'}
                buttonType="submit"
                disabled={saving || noParents}
                style={{ height: 44, fontSize: 14 }}
              />
              <Button
                text="Cancel"
                buttonType="button"
                type="secondary"
                onClick={onClose}
                style={{ height: 44, fontSize: 14 }}
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NodeModal;
